import { ArmchairIcon, BellIcon, Building2Icon, CalendarIcon, Grid3x3Icon, GroupIcon, HomeIcon, LandmarkIcon, LayoutDashboardIcon, LifeBuoyIcon, NetworkIcon, PackageIcon, PresentationIcon, ReceiptTextIcon, RotateCcwIcon, ScanIcon, ScanQrCodeIcon, ScrollTextIcon, SettingsIcon, ShieldIcon, UserIcon, FileSignatureIcon, WrenchIcon, ArchiveIcon } from 'lucide-react';
import * as Pages from '../app/pages';
import { STATIC_USER_ROLES, STATIC_USER_ROLES_ENUM } from '../utils/enums';
// import BellNewIcon from '../components/bell-new-icon';

export const RouteGroups = {
    MANAGEMENT: 'Management',
    SETTINGS: 'Settings',
}

export const URLS = {
    ADMIN: {
        ROOT: '/',
        HOME: '/home',
        EXECUTIVE: '/executive',
        FINANCE_ARCHIVE: '/finance/archive',
        AUTH: '/auth',
        AUTH_FORGOT_PASSWORD: '/auth/forgot-password',
        BRANCH: '/branch',
        BUILDING: '/building',
        FLOOR: '/floor',
        OFFICE: '/office',
        NOTIFICATIONS: '/notifications',
        MEETING_ROOM: '/meeting-room',
        INVENTORY: '/inventory',
        MAINTENANCE: '/maintenance',
        MAINTENANCE: '/maintenance',
        CO_WORKING: '/co-working',
        SEAT: '/seat',
        LEAD: '/lead',
        LEADS: '/leads',  // alias — QA/bookmarks often use plural
        CUSTOMER: '/customer',
        CONTRACTS: '/contracts',
        BOOKING: '/booking',
        USER: '/user',
        USER_GROUPS: '/user-groups',
        SUPPORT: '/support',
        SETTINGS: '/settings',
        SETTINGS_MEETING_ROOM: '/settings/meeting-room',
        SETTINGS_CSV: '/settings/csv-tools',
        SETTINGS_ROLES: '/settings/roles',
        SETTINGS_SUPPORT: '/settings/support',
        SETTINGS_QUOTATION: '/settings/quotation',
        SETTINGS_PAYMENTS: '/settings/payments',
        INVOICES: '/invoices',
        REFUNDS: '/refunds',
    },
    CUSTOMER: {
        HOME: '/c/home',
        PROFILE: '/c/profile',
        MEETING_ROOM: '/c/meeting-room',
        CONTRACT: '/c/contract',
        SUPPORT: '/c/support',
        NOTIFICATIONS: '/c/notifications',
        TEAM: '/c/team',
        INVOICES: '/c/invoices',
    },
    TEAM_MEMBER: {
        HOME: '/tm/home',
        PROFILE: '/tm/profile',
        MEETING_ROOM: '/tm/meeting-room',
        SUPPORT: '/tm/support',
        CONTRACT: '/tm/contract',
        SETTINGS: '/tm/settings',
        NOTIFICATIONS: '/tm/notifications',
    },
    RECEPTIONIST: {
        HOME: '/r/home',
        LEAD: '/r/lead',
        MEETING_ROOM: '/r/meeting-room',
        NOTIFICATIONS: '/r/notifications',
        PROFILE: '/r/profile',
        SUPPORT: '/r/support',
        SETTINGS: '/r/settings',
    },
    SUPPORT_AGENT: {
        HOME: '/sa/home',
        NOTIFICATIONS: '/sa/notifications',
    }
}

export const ProtectedRoutes = [
    {
        id: 'FX1',
        priority: 1,
        path: URLS.ADMIN.ROOT,
        page: Pages.Home,
        isPrivate: true,
        inSideBarList: true,
        name: 'Home',
        icon: <HomeIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['overview_cards', 'pie_charts', 'history_metrics', 'branch_summary'],
        group: null,
    },
    {
        id: 'FX2',
        priority: 2,
        path: URLS.ADMIN.HOME,
        page: Pages.Home,
        isPrivate: true,
        inSideBarList: false,
        name: 'Home',
        icon: <HomeIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['R'],
        group: null,
    },

    // Executive — static Admin/Manager/SuperAdmin via permissibleRoles; Staff via FX33 on dynamicRole
    // (Pulse prod uses role=staff + Administrator/Manager dynamic roles — no legacy role=admin users).
    // availableOperations FX33:view kept for Roles UI; Staff access checks route id FX33.
    {
        id: 'FX33',
        priority: 1.5,
        path: URLS.ADMIN.EXECUTIVE,
        page: Pages.Executive,
        isPrivate: true,
        inSideBarList: true,
        name: 'Executive',
        icon: <LayoutDashboardIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER],
        availableOperations: ['view'],
        group: null,
    },
    // Finance archive — Admins only (FX34). Staff via Administrator dynamic role / FX34.
    {
        id: 'FX34',
        priority: 1.6,
        path: URLS.ADMIN.FINANCE_ARCHIVE,
        page: Pages.FinanceArchive,
        isPrivate: true,
        inSideBarList: true,
        name: 'Finance Archive',
        icon: <ArchiveIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN],
        availableOperations: ['view'],
        group: null,
    },
    {
        id: 'FX3',
        path: URLS.ADMIN.AUTH,
        page: Pages.Auth,
        isPrivate: false,
        inSideBarList: false,
        name: 'Login',
        permissibleRoles: STATIC_USER_ROLES_ENUM,
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX3-1',
        path: URLS.ADMIN.AUTH_FORGOT_PASSWORD,
        page: Pages.ForgotPassword,
        isPrivate: false,
        inSideBarList: false,
        name: 'Forgot Password',
        permissibleRoles: STATIC_USER_ROLES_ENUM,
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX4',
        path: URLS.ADMIN.BRANCH,
        page: Pages.Branch,
        isPrivate: true,
        inSideBarList: true,
        name: 'Branches',
        icon: <NetworkIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.STAFF],
        availableOperations: ['view', 'edit'],
        group: RouteGroups.MANAGEMENT,
        nestedRoutes: [
            {
                id: 'FX4-1',
                path: `/:branchId`,
                page: Pages.BranchDetail,
                isPrivate: true,
                inSideBarList: false,
                name: 'Branch Details',
                icon: <NetworkIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
                availableOperations: ['R']
            }
        ]
    },
    {
        id: 'FX5',
        path: URLS.ADMIN.BUILDING,
        page: Pages.Building,
        isPrivate: true,
        inSideBarList: true,
        name: 'Buildings',
        icon: <Building2Icon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['view', 'edit'],
        group: RouteGroups.MANAGEMENT,
    },
    {
        id: 'FX6',
        path: URLS.ADMIN.FLOOR,
        page: Pages.Floor,
        isPrivate: true,
        inSideBarList: true,
        name: 'Floors',
        icon: <Grid3x3Icon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['view', 'edit'],
        group: RouteGroups.MANAGEMENT,
    },
    {
        id: 'FX7',
        path: URLS.ADMIN.OFFICE,
        page: Pages.Office,
        isPrivate: true,
        inSideBarList: true,
        name: 'Private Offices',
        icon: <ScanQrCodeIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['view', 'edit'],
        group: RouteGroups.MANAGEMENT,
    },
    {
        id: 'FX7-1',
        path: URLS.ADMIN.NOTIFICATIONS,
        page: Pages.Notifications,
        isPrivate: true,
        inSideBarList: true,
        name: 'Notifications',
        icon: <BellIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['manage'],
        group: null,
    },
    {
        id: 'FX29',
        path: URLS.SUPPORT_AGENT.HOME,
        page: Pages.SupportAgentHome,
        isPrivate: true,
        inSideBarList: true,
        name: 'Home',
        icon: <HomeIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.SUPPORT_AGENT],
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX30',
        path: URLS.SUPPORT_AGENT.NOTIFICATIONS,
        page: Pages.Notifications,
        isPrivate: true,
        inSideBarList: true,
        name: 'Notifications',
        icon: <BellIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.SUPPORT_AGENT],
        availableOperations: ['R', 'U'],
        group: null,
    },
    {
        id: 'FX8',
        path: URLS.ADMIN.MEETING_ROOM,
        page: Pages.MeetingRoom,
        isPrivate: true,
        inSideBarList: true,
        name: 'Meeting Rooms',
        icon: <PresentationIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.SUPPORT_AGENT, STATIC_USER_ROLES.STAFF],
        availableOperations: ['management', 'booking', 'future_booking'],
        group: null,
        nestedRoutes: [
            {
                id: 'FX8-bookings',
                path: '/bookings',
                page: Pages.MeetingRoom,
                isPrivate: true,
                inSideBarList: false,
                hideInRoleForm: true,
                name: 'Meeting Room Bookings',
                icon: <CalendarIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.SUPPORT_AGENT, STATIC_USER_ROLES.STAFF],
                availableOperations: ['manage']
            }
        ]
    },
    {
        id: 'FX31',
        path: URLS.ADMIN.INVENTORY,
        page: Pages.Inventory,
        isPrivate: true,
        inSideBarList: true,
        name: 'Inventory',
        icon: <PackageIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['view_dashboard', 'view_items', 'view_reports', 'manage_tab', 'verify', 'edit_items', 'log'],
        group: null,
        nestedRoutes: [
            {
                id: 'FX31-1',
                path: `/:itemId`,
                page: Pages.InventoryItemDetail,
                isPrivate: true,
                inSideBarList: false,
                name: 'Item Detail',
                icon: <PackageIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
                availableOperations: ['view_trend', 'audit_history']
            }
        ]
    },
    {
        id: 'FX32',
        path: URLS.ADMIN.MAINTENANCE,
        page: Pages.Maintenance,
        isPrivate: true,
        inSideBarList: true,
        name: 'Maintenance',
        icon: <WrenchIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['view', 'view_costs', 'create', 'edit', 'update_status', 'log_cost', 'comment', 'delete', 'export'],
        group: null,
        nestedRoutes: [
            {
                id: 'FX32-1',
                path: `/:workOrderId`,
                page: Pages.MaintenanceDetail,
                isPrivate: true,
                inSideBarList: false,
                name: 'Work Order Detail',
                icon: <WrenchIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
                availableOperations: ['view']
            }
        ]
    },
    {
        id: 'FX9',
        path: URLS.ADMIN.CO_WORKING,
        page: Pages.Coworking,
        isPrivate: true,
        inSideBarList: true,
        name: 'Shared Space',
        icon: <ScanIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['view', 'edit'],
        group: RouteGroups.MANAGEMENT,
    },
    {
        id: 'FX10',
        path: URLS.ADMIN.SEAT,
        page: Pages.Seat,
        isPrivate: true,
        inSideBarList: true,
        name: 'Seats',
        icon: <ArmchairIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['view', 'edit'],
        group: RouteGroups.MANAGEMENT,
        nestedRoutes: [
            {
                id: 'FX10-1',
                path: `/:seatId`,
                page: Pages.SeatDetail,
                isPrivate: true,
                inSideBarList: false,
                name: 'Seat Details',
                icon: <ArmchairIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
                availableOperations: ['R']
            }
        ]
    },
    {
        id: 'FX11',
        path: URLS.ADMIN.LEAD,
        page: Pages.Lead,
        isPrivate: true,
        inSideBarList: true,
        name: 'Leads',
        icon: <UserIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['manage', 'add_only'],
        group: null,
    },
    {
        id: 'FX12',
        path: URLS.ADMIN.CUSTOMER,
        page: Pages.Customer,
        isPrivate: true,
        inSideBarList: true,
        name: 'Customers',
        icon: <UserIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['view', 'edit'],
        group: null,
        nestedRoutes: [
            {
                id: 'FX12-1',
                path: `/:customerId`,
                page: Pages.CustomerDetail,
                isPrivate: true,
                inSideBarList: false,
                name: 'Customer Details',
                icon: <UserIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
                availableOperations: ['R', 'create_contract', 'impersonate'],
                nestedRoutes: [
                    {
                        id: 'FX12-1-1',
                        path: '/team-member/:teamMemberId',
                        page: Pages.TeamMemberDetail,
                        isPrivate: true,
                        inSideBarList: false,
                        name: 'Team Member Details',
                        icon: <UserIcon size={12} />,
                        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
                        availableOperations: ['view', 'edit', 'account_management']
                    }
                ]
            }
        ]
    },
    {
        id: 'FX13',
        path: URLS.ADMIN.CONTRACTS,
        page: Pages.Contract,
        isPrivate: true,
        inSideBarList: true,
        name: 'Contracts',
        icon: <ScrollTextIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['view', 'edit'],
        group: null,
    },
    {
        id: 'FX13-1',
        path: URLS.ADMIN.INVOICES,
        page: Pages.Invoice,
        isPrivate: true,
        inSideBarList: true,
        name: 'Invoices',
        icon: <ReceiptTextIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER],
        availableOperations: ['C', 'R', 'U'],
        group: null,
        nestedRoutes: [
            {
                id: 'FX13-1-1',
                path: '/:invoiceId',
                page: Pages.InvoiceDetail,
                isPrivate: true,
                inSideBarList: false,
                name: 'Invoice Details',
                icon: <ReceiptTextIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER],
                availableOperations: ['R']
            }
        ]
    },
    {
        id: 'FX13-2',
        path: URLS.ADMIN.REFUNDS,
        page: Pages.Refunds,
        isPrivate: true,
        inSideBarList: true,
        name: 'Refunds',
        icon: <RotateCcwIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER],
        availableOperations: ['C', 'R', 'U', 'D'],
        group: null,
    },
    {
        id: 'FX14',
        path: URLS.ADMIN.USER,
        page: Pages.User,
        isPrivate: true,
        inSideBarList: true,
        name: 'Users',
        icon: <UserIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
        availableOperations: ['manage'],
        group: null,
        nestedRoutes: [
            {
                id: 'FX14-1',
                path: `/:userId`,
                page: Pages.UserDetail,
                isPrivate: true,
                inSideBarList: false,
                name: 'User Details',
                icon: <UserIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.STAFF],
                availableOperations: ['R']
            }
        ]
    },
    {
        id: 'FX22-3',
        path: URLS.ADMIN.SETTINGS_ROLES,
        page: Pages.SettingsRoles,
        isPrivate: true,
        inSideBarList: true,
        name: 'Roles',
        icon: <ShieldIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.STAFF],
        availableOperations: ['manage'],
        group: RouteGroups.SETTINGS,
    },
    {
        id: 'FX16',
        path: URLS.CUSTOMER.HOME,
        page: Pages.CustomerHome,
        isPrivate: true,
        inSideBarList: true,
        name: 'Home',
        icon: <HomeIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.USER],
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX18-2',
        path: URLS.CUSTOMER.NOTIFICATIONS,
        page: Pages.CustomerNotifications,
        isPrivate: true,
        inSideBarList: true,
        name: 'Notifications',
        icon: <BellIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.USER],
        availableOperations: ['R', 'U'],
        group: null,
    },
    {
        id: 'FX17',
        path: URLS.CUSTOMER.PROFILE,
        page: Pages.CustomerProfile,
        isPrivate: true,
        inSideBarList: true,
        name: 'Profile',
        icon: <UserIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.USER],
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX17-1',
        path: URLS.CUSTOMER.MEETING_ROOM,
        page: Pages.CustomerMeetingRoom,
        isPrivate: true,
        inSideBarList: true,
        name: 'Meeting Rooms',
        icon: <PresentationIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.USER],
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX18',
        path: URLS.CUSTOMER.CONTRACT,
        page: Pages.CustomerContract,
        isPrivate: true,
        inSideBarList: true,
        name: 'Contracts',
        icon: <ScrollTextIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.USER],
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX18-3',
        path: URLS.CUSTOMER.INVOICES,
        page: Pages.CustomerInvoice,
        isPrivate: true,
        inSideBarList: true,
        name: 'Invoices',
        icon: <ReceiptTextIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.USER],
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX18-1',
        path: URLS.CUSTOMER.SUPPORT,
        page: Pages.CustomerSupport,
        isPrivate: true,
        inSideBarList: true,
        name: 'Support',
        icon: <LifeBuoyIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.USER], // TODO: Remove this once we have a proper auth system
        availableOperations: ['R', 'C'],
        group: null,
        // Use a "new" badge/icon to indicate this is a new feature
        // secondaryIcon: <BellNewIcon />,
        nestedRoutes: [
            {
                id: 'FX18-1-1',
                path: `/create`,
                page: Pages.CustomerSupportCrudForm,
                isPrivate: true,
                inSideBarList: false,
                name: 'Create Ticket',
                icon: <LifeBuoyIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.USER], // TODO: Remove this once we have a proper auth system
                availableOperations: ['C']
            },
            {
                id: 'FX18-1-2',
                path: `/:ticketId`,
                page: Pages.CustomerSupportDetail,
                isPrivate: true,
                inSideBarList: false,
                name: 'Ticket Details',
                icon: <LifeBuoyIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.USER], // TODO: Remove this once we have a proper auth system
                availableOperations: ['R']
            }
        ]
    },

    {
        id: 'FX18-3',
        path: URLS.CUSTOMER.TEAM,
        page: Pages.CustomerTeam,
        isPrivate: true,
        inSideBarList: true,
        name: 'Manage Team',
        icon: <GroupIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.USER],
        availableOperations: ['R', 'C', 'U', 'D'],
        group: null,
        nestedRoutes: [
            {
                id: 'FX18-3-1',
                path: `/:teamMemberId`,
                page: Pages.CustomerTeamMemberDetail,
                isPrivate: true,
                inSideBarList: false,
                name: 'Team Member Details',
                icon: <GroupIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.USER],
                availableOperations: ['R', 'U']
            }
        ]
    },
    {
        id: 'FX19',
        path: URLS.TEAM_MEMBER.HOME,
        page: Pages.TeamMemberHome,
        isPrivate: true,
        inSideBarList: true,
        name: 'Home',
        icon: <HomeIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.TEAM_MEMBER],
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX19-1',
        path: URLS.TEAM_MEMBER.PROFILE,
        page: Pages.CustomerProfile,
        isPrivate: true,
        inSideBarList: false,
        name: 'Profile',
        icon: <UserIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.TEAM_MEMBER],
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX20',
        path: URLS.TEAM_MEMBER.MEETING_ROOM,
        page: Pages.TeamMemberMeetingRoom,
        isPrivate: true,
        inSideBarList: true,
        name: 'Meeting Rooms',
        icon: <PresentationIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.TEAM_MEMBER],
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX20-1',
        path: URLS.TEAM_MEMBER.NOTIFICATIONS,
        page: Pages.TeamMemberNotifications,
        isPrivate: true,
        inSideBarList: true,
        name: 'Notifications',
        icon: <BellIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.TEAM_MEMBER],
        availableOperations: ['R', 'U'],
        group: null,
    },
    {
        id: 'FX20-2',
        path: URLS.TEAM_MEMBER.CONTRACT,
        page: Pages.CustomerContract,
        isPrivate: true,
        inSideBarList: true,
        name: 'Contracts',
        icon: <ScrollTextIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.TEAM_MEMBER],
        requiredAllowedPage: 'contracts',
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX20-3',
        path: URLS.TEAM_MEMBER.SUPPORT,
        page: Pages.CustomerSupport,
        isPrivate: true,
        inSideBarList: true,
        name: 'Support',
        icon: <LifeBuoyIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.TEAM_MEMBER],
        requiredAllowedPage: 'support',
        availableOperations: ['R', 'C'],
        group: null,
        nestedRoutes: [
            {
                id: 'FX20-3-1',
                path: `/create`,
                page: Pages.CustomerSupportCrudForm,
                isPrivate: true,
                inSideBarList: false,
                name: 'Create Ticket',
                icon: <LifeBuoyIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.TEAM_MEMBER],
                requiredAllowedPage: 'support',
                availableOperations: ['C']
            },
            {
                id: 'FX20-3-2',
                path: `/:ticketId`,
                page: Pages.CustomerSupportDetail,
                isPrivate: true,
                inSideBarList: false,
                name: 'Ticket Details',
                icon: <LifeBuoyIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.TEAM_MEMBER],
                requiredAllowedPage: 'support',
                availableOperations: ['R']
            }
        ]
    },
    {
        id: 'FX21',
        path: URLS.ADMIN.SUPPORT,
        page: Pages.Support,
        isPrivate: true,
        inSideBarList: true,
        name: 'Support',
        icon: <LifeBuoyIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.SUPPORT_AGENT, STATIC_USER_ROLES.STAFF],
        availableOperations: ['manage'],
        group: null,
        nestedRoutes: [
            {
                id: 'FX21-1',
                path: `/:ticketId`,
                page: Pages.SupportDetail,
                isPrivate: true,
                inSideBarList: false,
                name: 'Ticket Details',
                icon: <LifeBuoyIcon size={12} />,
                permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.SUPPORT_AGENT, STATIC_USER_ROLES.STAFF],
                availableOperations: ['R'],
            }
        ]
    },
    {
        id: 'FX22',
        path: URLS.ADMIN.SETTINGS,
        page: Pages.Settings,
        isPrivate: true,
        inSideBarList: false,
        name: 'Settings',
        icon: <SettingsIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.STAFF],
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX22-1',
        path: URLS.ADMIN.SETTINGS_MEETING_ROOM,
        page: Pages.SettingsMeetingRoom,
        isPrivate: true,
        inSideBarList: true,
        name: 'Meeting Room',
        icon: <SettingsIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.STAFF],
        availableOperations: ['manage'],
        group: RouteGroups.SETTINGS,
    },
    {
        id: 'FX22-2',
        path: URLS.ADMIN.SETTINGS_CSV,
        page: Pages.SettingsCsvTools,
        isPrivate: true,
        inSideBarList: true,
        name: 'CSV Tools',
        icon: <ScrollTextIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.STAFF],
        availableOperations: ['manage'],
        group: RouteGroups.SETTINGS,
    },
    {
        id: 'FX22-4',
        path: URLS.ADMIN.SETTINGS_SUPPORT,
        page: Pages.SettingsSupport,
        isPrivate: true,
        inSideBarList: true,
        name: 'Support',
        icon: <LifeBuoyIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.STAFF],
        availableOperations: ['manage'],
        group: RouteGroups.SETTINGS,
    },
    {
        id: 'FX22-5',
        path: URLS.ADMIN.SETTINGS_QUOTATION,
        page: Pages.SettingsQuotation,
        isPrivate: true,
        inSideBarList: true,
        name: 'Quotation',
        icon: <FileSignatureIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.STAFF],
        availableOperations: ['manage'],
        group: RouteGroups.SETTINGS,
    },
    {
        id: 'FX22-3',
        path: URLS.ADMIN.SETTINGS_PAYMENTS,
        page: Pages.SettingsPayments,
        isPrivate: true,
        inSideBarList: true,
        name: 'Payments',
        icon: <LandmarkIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.SUPER_ADMIN, STATIC_USER_ROLES.STAFF],
        availableOperations: ['manage'],
        group: RouteGroups.SETTINGS,
    },
    {
        id: 'FX23',
        path: URLS.RECEPTIONIST.HOME,
        page: Pages.ReceptionistHome,
        isPrivate: true,
        inSideBarList: true,
        name: 'Home',
        icon: <HomeIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.RECEPTIONIST],
        availableOperations: ['R'],
        group: null,
    },
    {
        id: 'FX25',
        path: URLS.RECEPTIONIST.LEAD,
        page: Pages.ReceptionistLead,
        isPrivate: true,
        inSideBarList: true,
        name: 'Leads',
        icon: <UserIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.RECEPTIONIST],
        availableOperations: ['C'],
        group: null,
    },
    {
        id: 'FX24',
        path: URLS.RECEPTIONIST.MEETING_ROOM,
        page: Pages.ReceptionistMeetingRoom,
        isPrivate: true,
        inSideBarList: true,
        name: 'Meeting Rooms',
        icon: <PresentationIcon size={16} />,
        permissibleRoles: [STATIC_USER_ROLES.RECEPTIONIST],
        availableOperations: ['R'],
        group: null,
    },
    // {
    //     id: 'FX25',
    //     path: URLS.RECEPTIONIST.NOTIFICATIONS,
    //     page: Pages.ReceptionistNotifications,
    //     isPrivate: true,
    //     inSideBarList: true,
    //     name: 'Notifications',
    //     icon: <BellIcon size={16} />,
    //     permissibleRoles: [STATIC_USER_ROLES.RECEPTIONIST],
    //     availableOperations: ['R', 'U'],
    //     group: null,
    // },
    // {
    //     id: 'FX26',
    //     path: URLS.RECEPTIONIST.PROFILE,
    //     page: Pages.ReceptionistProfile,
    //     isPrivate: true,
    //     inSideBarList: true,
    //     name: 'Profile',
    //     icon: <UserIcon size={16} />,
    //     permissibleRoles: [STATIC_USER_ROLES.RECEPTIONIST],
    //     group: null,
    // },
    // {
    //     id: 'FX27',
    //     path: URLS.RECEPTIONIST.SUPPORT,
    //     page: Pages.ReceptionistSupport,
    //     isPrivate: true,
    //     inSideBarList: true,
    //     name: 'Support',
    //     icon: <LifeBuoyIcon size={16} />,
    //     permissibleRoles: [STATIC_USER_ROLES.RECEPTIONIST],
    //     group: null,
    // },
    // {
    //     id: 'FX28',
    //     path: URLS.RECEPTIONIST.SETTINGS,
    //     page: Pages.ReceptionistSettings,
    //     isPrivate: true,
    //     inSideBarList: true,
    //     name: 'Settings',
    //     icon: <SettingsIcon size={16} />,
    //     permissibleRoles: [STATIC_USER_ROLES.RECEPTIONIST],
    // }
]